import { useMemo, useState } from "react";
import type { Identifier } from "ra-core";
import { Crop, ShieldCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import type {
  EchoflicksSessionFeedback,
  EchoflicksSessionJourney,
} from "../../types";
import { feedbackRecordToSessionFeedback } from "../../feedback/framingFeedback";
import { useEchoFeedbackReports } from "../../feedback/useEchoFeedbackReports";
import { buildFramingScenes } from "../framingDiagnostics";
import { CropPipelinePanel } from "./CropPipelinePanel";
import { CropValidationPanel } from "./CropValidationPanel";
import { FramingSceneRail } from "./FramingSceneRail";
import { FramingSummaryBar } from "./FramingSummaryBar";
import { buildCropValidationModel } from "./cropValidationModel";
import {
  buildFramingSectionModel,
  filterRankedScenes,
  summarizeFramingFeedback,
  type FramingSectionFilter,
} from "./framingSectionModel";

/**
 * First-class Framing & crop section on Echo detail (CROP-WB-005).
 *
 * Loads only already-available session/framing summary data: zero scene
 * inspector calls and zero private media requests until the operator
 * explicitly chooses Inspect crop pixels.
 */
export function FramingSection({
  sessionId,
  journey,
  feedback,
  isAdmin,
  onInspectScene,
}: {
  sessionId: Identifier;
  journey?: EchoflicksSessionJourney | null;
  feedback: EchoflicksSessionFeedback[];
  isAdmin: boolean;
  onInspectScene: (sceneId: string) => void;
}) {
  const [filter, setFilter] = useState<FramingSectionFilter>("all");
  const [tab, setTab] = useState("scenes");
  const reviewerFeedbackQuery = useEchoFeedbackReports(
    isAdmin ? String(sessionId) : null,
  );
  const reviewerReports = useMemo(
    () =>
      (reviewerFeedbackQuery.data?.data ?? []).filter(
        (report) => report.echo.id === String(sessionId),
      ),
    [reviewerFeedbackQuery.data?.data, sessionId],
  );
  const framingEvents = useMemo(
    () => journey?.framing ?? [],
    [journey?.framing],
  );
  const combinedFeedback = useMemo(
    () => [
      ...feedback,
      ...reviewerReports.map((report) =>
        feedbackRecordToSessionFeedback(report, framingEvents),
      ),
    ],
    [feedback, framingEvents, reviewerReports],
  );
  const scenes = useMemo(
    () => buildFramingScenes(framingEvents),
    [framingEvents],
  );
  const feedbackSummary = useMemo(
    () =>
      summarizeFramingFeedback(
        combinedFeedback,
        reviewerReports,
        framingEvents,
      ),
    [combinedFeedback, framingEvents, reviewerReports],
  );
  const model = useMemo(
    () => buildFramingSectionModel(scenes, feedbackSummary),
    [feedbackSummary, scenes],
  );
  const filtered = useMemo(
    () => filterRankedScenes(model.ranked, filter),
    [filter, model.ranked],
  );
  const validation = useMemo(
    () => buildCropValidationModel(scenes, model),
    [model, scenes],
  );

  if (!isAdmin) return null;

  const summary = journey?.summary?.framing;
  const schemaBadge =
    scenes.length === 0
      ? "No framing telemetry"
      : scenes.some((scene) => scene.planDigest)
        ? "framing-v2 evidence"
        : "Legacy framing evidence";
  const showRiskyScenes = () => {
    setFilter("all");
    setTab("scenes");
  };
  const inspectFirstRisk = () => {
    if (validation.firstRiskSceneId) {
      onInspectScene(validation.firstRiskSceneId);
    }
  };

  return (
    <Card id="framing-crop-section">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Crop className="size-4" />
              Crop Debug
            </CardTitle>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Inspect the crop evidence chain, compare rendered pixels, and
              repeat the same validation after a framing change.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{schemaBadge}</Badge>
            {summary?.status ? (
              <Badge
                variant={summary.at_risk ? "destructive" : "secondary"}
                className="capitalize"
              >
                {summary.status.replaceAll("_", " ")}
              </Badge>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList aria-label="Crop debugging views">
            <TabsTrigger value="scenes">Scenes</TabsTrigger>
            <TabsTrigger value="validate">Validate changes</TabsTrigger>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          </TabsList>

          <TabsContent value="scenes" className="mt-4 space-y-4">
            {scenes.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No subject-aware framing telemetry exists for this Echo. Exact
                crop geometry is unavailable rather than reconstructed.
              </p>
            ) : (
              <>
                <FramingSummaryBar
                  model={model}
                  filter={filter}
                  onFilterChange={setFilter}
                />
                {feedbackSummary.uncorrelatedCount > 0 ? (
                  <div
                    className="rounded-md border border-amber-300/60 bg-amber-50 p-3 text-sm dark:bg-amber-950/30"
                    role="note"
                  >
                    <strong>
                      {feedbackSummary.uncorrelatedCount} uncorrelated crop
                      feedback report
                      {feedbackSummary.uncorrelatedCount === 1 ? "" : "s"}.
                    </strong>{" "}
                    No scene can be trusted as the match; the reports keep their
                    delivered timestamp and screenshot only.
                  </div>
                ) : null}
                <FramingSceneRail
                  scenes={filtered}
                  filter={filter}
                  onInspect={onInspectScene}
                />
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ShieldCheck className="size-3.5" />
                  Inspect crop pixels opens private customer media. Access is
                  restricted to administrators, uses short-lived links, and is
                  recorded in the evidence access audit.
                </p>
              </>
            )}
          </TabsContent>

          <TabsContent value="validate" className="mt-4">
            <CropValidationPanel
              validation={validation}
              onInspectRisk={inspectFirstRisk}
              onShowRisk={showRiskyScenes}
            />
          </TabsContent>

          <TabsContent value="pipeline" className="mt-4">
            <CropPipelinePanel />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
